import React, { useEffect, useState } from "react";
import {
	TextField,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Box,
	Avatar,
	Typography,
	Paper,
	Grid,
	Zoom,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SaveIcon from "@mui/icons-material/Save";
import api from "../Api/base";
import Api from "../Api/Api.json";
import { setLoading } from "../App";
import { LoadingButton } from "@mui/lab";

const UploadIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="17 8 12 3 7 8"></polyline>
		<line x1="12" y1="3" x2="12" y2="15"></line>
	</svg>
);

const Profile = () => {
	const [profile, setProfile] = useState(null);
	const [loadingButton, setLoadingButton] = useState(false);
	const [errors, setErrors] = useState({});
	const [previewImage, setPreviewImage] = useState(null);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setProfile({
			...profile,
			[name]: value,
		});

		// Clear error when field is being edited
		if (errors[name]) {
			setErrors({
				...errors,
				[name]: null,
			});
		}
	};

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setProfile({
				...profile,
				profilePicture: file,
			});

			// Create preview URL
			const reader = new FileReader();
			reader.onloadend = () => {
				setPreviewImage(reader.result);
			};
			reader.readAsDataURL(file);
		}
	};

	const validateForm = () => {
		const newErrors = {};

		// Required fields validation
		if (!profile.name.trim()) newErrors.name = "Name is required";
		if (!profile.age) newErrors.age = "Age is required";
		if (!profile.gender) newErrors.gender = "Gender is required";
		if (!profile.phone) newErrors.phone = "Phone is required";

		// // Age validation
		// if (profile.age && (isNaN(profile.age) || parseInt(profile.age) < 1)) {
		// 	newErrors.age = "Please enter a valid age";
		// }

		// // Phone validation
		// const phoneRegex = /^\+?[0-9]{10,15}$/;
		// if (profile.phone && !phoneRegex.test(profile.phone)) {
		// 	newErrors.phone = "Please enter a valid phone number";
		// }

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (validateForm()) {
			// Here you would typically send the data to your backend
			console.log("Form submitted:", profile);
			// Update profile
			await updateProfile();
		}
	};

	const getUserID = async () => {
		if (!profile?.id) {
			const result = await api.post(
				"https://tib60ktrrj.execute-api.us-east-1.amazonaws.com/prod",
				{
					task: "getUserInfo",
				}
			);
			if (result.success) {
				return result.data[0].id;
			} else {
				return 0;
			}
		}
		return profile.id;
	};

	const fetchProfile = async () => {
		const id = await getUserID();

		const result = await api.post(Api.PATIENT_API, {
			task: "getPatientProfile",
			patient_id: id,
		});

		if (result.success && result.data.length) {
			const data = result.data[0];
			setProfile({
				name: data.name,
				age: data.age,
				gender:
					data.gender === "M"
						? "male"
						: data.gender === "F"
						? "female"
						: data.gender,
				phone: data.phone,
				address: data.address,
			});
		} else {
			console.log("Failed to fetch profile data");
		}

		setLoading(false);
	};

	const updateProfile = async () => {
		setLoadingButton(true);
		const id = await getUserID();
		const result = await api.post(Api.PATIENT_API, {
			task: "editPatientProfile",
			patient_id: id,
			name: profile.name,
			age: profile.age,
			gender: profile.gender,
			phone: profile.phone,
			address: profile.address,
		});
		if (result.success) {
			alert("Profile saved successfully!");
		}
		setLoadingButton(false);
	};

	useEffect(() => {
		fetchProfile();
	}, []);
	return (
		<div
			className="max-w-4xl mx-auto px-5 py-3"
			style={{
				minHeight: `calc(100vh - 64px)`,
			}}
		>
			<h1 className="text-3xl font-bold text-center mt-4 mb-6 top-0">
				আমার প্রোফাইল
			</h1>
			{profile !== null && (
				<Zoom in={true}>
					<Paper elevation={3} className="flex flex-col w-full gap-3">
						<form onSubmit={handleSubmit}>
							<Box className="flex flex-col md:flex-row">
								{/* Profile Picture Section */}
								<Box
									className="flex flex-col items-center w-full md:w-1/3 justify-center p-8 border-r-2 border-gray-300 
								bg-zinc-500
								"
								>
									<Box className="mb-4 flex flex-col items-center">
										<Avatar
											src={previewImage}
											sx={{
												width: 150,
												height: 150,
												mb: 2,
												border: "3px solid white", // White border to make avatar pop against black
											}}
										/>

										<input
											accept="image/*"
											style={{ display: "none" }}
											id="profile-picture-input"
											type="file"
											onChange={handleImageChange}
										/>
										<label htmlFor="profile-picture-input">
											<Button
												variant="contained"
												startIcon={<UploadIcon />}
												className="normal-case"
												fullWidth
												// sx={{
												// 	color: "white",
												// 	borderColor: "white",
												// 	"&:hover": {
												// 		borderColor: "#e0e0e0",
												// 		backgroundColor:
												// 			"rgba(255, 255, 255, 0.08)",
												// 	},
												// }}
											>
												Upload Picture
											</Button>
										</label>
									</Box>
								</Box>

								{/* Form Fields Section */}
								<Box className="w-full md:w-2/3 flex flex-col gap-4 p-8">
									<Box className="flex flex-col gap-3">
										{/* Name */}
										<TextField
											fullWidth
											label="Name"
											name="name"
											value={profile.name}
											onChange={handleChange}
											error={!!errors.name}
											helperText={errors.name}
											required
											variant="outlined"
											size="small"
											// placeholder="Name"
											multiline
											rows={1}
										/>

										{/* Age */}
										<Box className="flex flex-col md:flex-row gap-3">
											<TextField
												fullWidth
												label="Age"
												name="age"
												type="number"
												value={profile.age}
												onChange={handleChange}
												error={!!errors.age}
												helperText={errors.age}
												required
												variant="outlined"
												size="small"
											/>
											<FormControl
												fullWidth
												required
												error={!!errors.gender}
												size="small"
											>
												<InputLabel id="gender-label">
													Gender
												</InputLabel>
												<Select
													labelId="gender-label"
													name="gender"
													value={profile.gender}
													onChange={handleChange}
													label="Gender"
													size="small"
												>
													<MenuItem value="male">
														পুরুষ
													</MenuItem>
													<MenuItem value="female">
														মহিলা
													</MenuItem>
													<MenuItem value="other">
														অন্যান্য
													</MenuItem>
												</Select>
												{errors.gender && (
													<Typography
														color="error"
														variant="caption"
													>
														{errors.gender}
													</Typography>
												)}
											</FormControl>
										</Box>

										{/* Phone */}
										<TextField
											fullWidth
											label="Phone"
											name="phone"
											type="text"
											value={profile.phone}
											onChange={handleChange}
											error={!!errors.phone}
											helperText={errors.phone}
											required
											variant="outlined"
											multiline
											rows={1}
											size="small"
										/>

										{/* Address */}
										<TextField
											fullWidth
											label="Address"
											name="address"
											value={profile.address}
											onChange={handleChange}
											variant="outlined"
											multiline
											rows={2}
											size="small"
										/>
									</Box>

									{/* Submit Button */}
									<Box className="flex justify-end">
										<LoadingButton
											type="submit"
											variant="contained"
											color="primary"
											size="large"
											startIcon={<SaveIcon />}
											loadingPosition="start"
											className="normal-case"
											loading={loadingButton}
										>
											Save Profile
										</LoadingButton>
									</Box>
								</Box>
							</Box>
						</form>
					</Paper>
				</Zoom>
			)}
		</div>
	);
};

export default Profile;
